import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Box, IconButton, Typography, useMediaQuery } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import { ResponsivePie } from "@nivo/pie";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useCompanyRevenueProductSegmentation } from "../hooks/useCompanyRevenueProductSegmentation";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { formatNumber } from "../utils/formatNumber";
import { getApiPeriodParam, ViewMode } from "./shared/financialPeriodConfig";
import ChartHeader from "./shared/ChartHeader";
import ChartModal from "./shared/ChartModal";
import ChartTooltipPortal from "./shared/ChartTooltipPortal";

/**
 * Build the segment colour palette from the MUI theme so that the Insight
 * green, red, amber, and purple family are always used consistently, with
 * supplementary flat-UI colours filling out the rest of the slots.
 */
const getSegmentColors = (theme: Theme): string[] => [
  theme.palette.primary.main,        // Insight green — most prominent segment
  "#3b82f6",                         // Blue
  theme.palette.warning.main,        // Amber
  theme.palette.error.main,          // Red
  "#8b5cf6",                         // Violet
  "#06b6d4",                         // Cyan
  "#f97316",                         // Orange
  "#ec4899",                         // Pink
  "#84cc16",                         // Lime
  "#6366f1",                         // Indigo
  "#14b8a6",                         // Teal
  "#f43f5e",                         // Rose
  "#10b981",                         // Emerald
  "#0ea5e9",                         // Sky
  "#d946ef",                         // Fuchsia
  "#eab308",                         // Yellow
];

interface PieSlice {
  id: string;
  label: string;
  value: number;
  color: string;
  formattedValue: string;
  percentage: number;
}

interface RevenueSegmentationChartProps {
  ticker: string;
  height?: number;
  viewMode?: ViewMode;
  isModal?: boolean;
  onCloseModal?: () => void;
  renderCard?: boolean;
  showFullscreen?: boolean;
}

const RevenueSegmentationChart: React.FC<RevenueSegmentationChartProps> = ({
  ticker,
  height = 340,
  viewMode = "quarterly",
  isModal = false,
  onCloseModal,
  renderCard = true,
  showFullscreen = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { currencySymbol } = useStockCurrency();
  const surfaceBg = theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.02)";

  const segmentColors = useMemo(() => getSegmentColors(theme), [theme]);

  const period = getApiPeriodParam(viewMode);
  const { data, isLoading, error } = useCompanyRevenueProductSegmentation(ticker, period);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const pieContainerRef = useRef<HTMLDivElement>(null);

  // Mobile-only: tap-toggled tooltip state. On mobile Nivo's hover-based
  // tooltip is bypassed because hover doesn't persist after touchend.
  const [mobileTooltip, setMobileTooltip] = useState<{ slice: PieSlice; x: number; y: number } | null>(null);
  const mobileTouchOriginRef = useRef<{ y: number } | null>(null);

  // Sort oldest → newest so prev/next navigation is chronologically intuitive
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...data].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }, [data]);

  // Default to the latest period when data arrives or the period type changes
  useEffect(() => {
    if (sortedData.length > 0) {
      setCurrentIndex(sortedData.length - 1);
    }
  }, [sortedData.length]);

  const currentPeriod = sortedData[currentIndex];

  // Close mobile tooltip on scroll or tap outside the pie container.
  useEffect(() => {
    if (!isMobile || !mobileTooltip) return;

    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) mobileTouchOriginRef.current = { y: t.clientY };
      if (!pieContainerRef.current?.contains(e.target as Node)) {
        setMobileTooltip(null);
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || !mobileTouchOriginRef.current) return;
      if (Math.abs(t.clientY - mobileTouchOriginRef.current.y) > 10) {
        setMobileTooltip(null);
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, [isMobile, mobileTooltip]);

  // Shared tooltip content used by both the desktop hover portal (via Nivo)
  // and the mobile tap portal rendered at the component level.
  const renderTooltipContent = (slice: PieSlice) => (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1.5,
        overflow: "hidden",
        maxWidth: 320,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.75,
          backgroundColor: `${slice.color}1a`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: slice.color,
            flexShrink: 0,
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 500,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
          }}
        >
          {slice.label}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 3,
          px: 1.5,
          py: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "1.1rem",
            fontWeight: 700,
            color: theme.palette.text.primary,
            whiteSpace: "nowrap",
          }}
        >
          {slice.formattedValue}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary, whiteSpace: "nowrap" }}
        >
          {slice.percentage?.toFixed(1) ?? "0.0"}%
        </Typography>
      </Box>
    </Box>
  );

  // Colours are assigned by size rank (largest → smallest) so the dominant
  // segment always gets the primary green, second always blue, etc.
  const pieData = useMemo((): PieSlice[] => {
    if (!currentPeriod) return [];
    const total = Object.values(currentPeriod.data).reduce((s, v) => s + v, 0);
    return Object.entries(currentPeriod.data)
      .filter(([, value]) => value > 0)
      .sort(([, a], [, b]) => b - a)
      .map(([name, value], i) => ({
        id: name,
        label: name,
        value,
        color: segmentColors[i % segmentColors.length],
        formattedValue: `${currencySymbol}${formatNumber(value)}`,
        percentage: total > 0 ? (value / total) * 100 : 0,
      }));
  }, [currentPeriod, segmentColors, currencySymbol]);

  const periodLabel = useMemo(() => {
    if (!currentPeriod) return "";
    const { period: p, fiscalYear } = currentPeriod;
    return p === "FY" ? `FY${fiscalYear}` : `${p} FY${fiscalYear}`;
  }, [currentPeriod]);

  const handlePrev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const handleNext = () =>
    setCurrentIndex((i) => Math.min(sortedData.length - 1, i + 1));

  const navigationActions = (
    <Box display="flex" alignItems="center" gap={0.5}>
      <IconButton
        size="small"
        onClick={handlePrev}
        disabled={currentIndex === 0}
        sx={{
          color: currentIndex === 0 ? theme.palette.text.disabled : theme.palette.primary.main,
          p: 0.5,
          "&:hover": { backgroundColor: "rgba(34, 197, 94, 0.08)" },
        }}
        aria-label="Previous period"
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>

      <Box
        sx={{
          px: 1.5,
          py: 0.25,
          borderRadius: 1,
          backgroundColor: surfaceBg,
          border: `1px solid ${theme.palette.divider}`,
          minWidth: isMobile ? 70 : 90,
          textAlign: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.primary, fontWeight: 600, whiteSpace: "nowrap" }}
        >
          {periodLabel || "—"}
        </Typography>
      </Box>

      <IconButton
        size="small"
        onClick={handleNext}
        disabled={currentIndex === sortedData.length - 1}
        sx={{
          color:
            currentIndex === sortedData.length - 1
              ? theme.palette.text.disabled
              : theme.palette.primary.main,
          p: 0.5,
          "&:hover": { backgroundColor: "rgba(34, 197, 94, 0.08)" },
        }}
        aria-label="Next period"
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  const chartContent = (
    <>
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        // No gap — legend text sits flush against the pie's right edge
        gap: 0,
        height: isMobile ? "auto" : isModal ? "100%" : height,
        minHeight: isModal ? 400 : undefined,
      }}
    >
      {/* Donut chart — flat UI: no shadows, no gradients, no arc labels */}
      <Box
        ref={pieContainerRef}
        onClick={isMobile ? () => setMobileTooltip(null) : undefined}
        sx={{
          flex: isMobile ? "0 0 auto" : "0 0 38%",
          height: isMobile ? 220 : "100%",
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <ResponsivePie
          data={pieData}
          animate={false}
          colors={pieData.map((d) => d.color)}
          innerRadius={0.52}
          padAngle={1.2}
          cornerRadius={0}
          borderWidth={0}
          activeOuterRadiusOffset={0}
          enableArcLabels={false}
          enableArcLinkLabels={false}
          // Trim the right Nivo margin so the donut sits close to the legend
          margin={{ top: 16, right: 0, bottom: 16, left: 16 }}
          // On mobile, Nivo's hover tooltip is disabled — the tap-toggle
          // mechanism below manages the tooltip at the component level instead.
          // On desktop, hover via ChartTooltipPortal works normally.
          tooltip={isMobile ? () => null : ({ datum }) => (
            <ChartTooltipPortal containerRef={pieContainerRef}>
              {renderTooltipContent(datum.data as PieSlice)}
            </ChartTooltipPortal>
          )}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onClick={(datum: any, event: React.MouseEvent) => {
            if (!isMobile) return;
            // Stop propagation so the pieContainerRef onClick (which dismisses
            // the tooltip on empty-area taps) doesn't also fire for slice taps.
            event.stopPropagation();
            const slice = datum.data as PieSlice;
            setMobileTooltip(prev =>
              prev?.slice.id === slice.id ? null : { slice, x: event.clientX, y: event.clientY }
            );
          }}
          theme={{
            background: "transparent",
            text: { fontSize: 12, fill: theme.palette.text.primary },
            tooltip: {
              container: {
                background: "transparent",
                border: "none",
                borderRadius: 0,
                boxShadow: "none",
                padding: 0,
              },
            },
          }}
        />
      </Box>

      {/* Scrollable legend — sits flush against the pie, left-padded for breathing room */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          maxHeight: isMobile ? 240 : undefined,
          pt: isMobile ? 1 : 0.5,
          pb: 1,
          pl: 1,
          // Subtle left divider separating legend from pie area (desktop only)
          borderLeft: isMobile ? "none" : `1px solid ${theme.palette.divider}`,
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: theme.palette.divider,
            borderRadius: 2,
          },
        }}
      >
        {pieData.map((slice) => (
          <Box
            key={slice.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              py: 0.75,
              px: 1,
              borderRadius: 1,
              transition: "background-color 0.15s ease",
              "&:hover": { backgroundColor: surfaceBg },
            }}
          >
            {/* Colour swatch */}
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "2px",
                backgroundColor: slice.color,
                flexShrink: 0,
              }}
            />

            {/* Segment name — truncates so values stay close */}
            <Typography
              variant="body2"
              noWrap
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 500,
                flex: 1,
                minWidth: 0,
              }}
              title={slice.label}
            >
              {slice.label}
            </Typography>

            {/* Value and percentage with generous spacing between them */}
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 2,
                flexShrink: 0,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.primary, fontWeight: 700, whiteSpace: "nowrap" }}
              >
                {slice.formattedValue}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  whiteSpace: "nowrap",
                  minWidth: 40,
                  textAlign: "right",
                }}
              >
                {slice.percentage?.toFixed(1) ?? "0.0"}%
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>

    {/* Mobile persistent tooltip — rendered outside the Nivo SVG so it
        survives after touchend, positioned at the tap point and clamped
        to the viewport. Closed by scroll or tap-outside (see useEffect). */}
    {isMobile && mobileTooltip && ReactDOM.createPortal(
      <div
        style={{
          position: "fixed",
          left: Math.max(148, Math.min(mobileTooltip.x, window.innerWidth - 148)),
          top: mobileTooltip.y - 24,
          transform: "translate(-50%, -100%)",
          zIndex: 99999,
          pointerEvents: "none",
        }}
      >
        {renderTooltipContent(mobileTooltip.slice)}
      </div>,
      document.body
    )}
    </>
  );

  if (isLoading || error || sortedData.length === 0) return null;

  const inner = (
    <>
      <ChartHeader
        title="Revenue Segmentation"
        showFullscreen={showFullscreen}
        isModal={isModal}
        onOpenModal={() => setModalOpen(true)}
        onCloseModal={onCloseModal}
        actions={navigationActions}
      />
      {chartContent}
    </>
  );

  return (
    <>
      {renderCard ? (
        <Box
          sx={{
            backgroundColor: theme.palette.background.surface,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            p: 2,
            ...(isModal && { height: "100%", display: "flex", flexDirection: "column" }),
          }}
        >
          {inner}
        </Box>
      ) : inner}

      <ChartModal open={modalOpen} onClose={() => setModalOpen(false)}>
        <RevenueSegmentationChart
          ticker={ticker}
          height={600}
          viewMode={viewMode}
          isModal={true}
          renderCard={false}
          showFullscreen={false}
          onCloseModal={() => setModalOpen(false)}
        />
      </ChartModal>
    </>
  );
};

export default RevenueSegmentationChart;
